import React, { useMemo, useState } from 'react';

import {
  Avatar,
  AvatarGroup,
  AvatarProps,
  ChakraBox,
  ChakraButton,
  ChakraFlex,
  CircularProgress,
  Counter,
  EquipUIFireflyV2Theme,
  FAIconName,
  Icon,
  Tag,
  TagColor,
  Text,
  Tooltip,
} from '@equip.health/ui';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { UserMessage } from '@sendbird/chat/message';

import {
  getAdminMessageText,
  getUnreadMessagesCount,
  getUserTypeFromChatUser,
  isExternalChannel,
} from '~/lib/util/chat/chat.utils';
import { getChannelName } from '~/lib/util/chat/chatChannel.util';
import { DateTime } from 'luxon';
import { ChannelMetaData } from '~/lib/types/chat';
import { useDatadog } from '~/lib/hooks/useDatadog';

interface ProviderChannelTileProps {
  channel: GroupChannel;
  hideGroupMembersIcon?: boolean;
  id: string;
  onClick: (channel: GroupChannel) => void;
  showMarkAsRead?: boolean;
  showPinned?: boolean;
}

const { neutral } = EquipUIFireflyV2Theme.colors;

const idPrefix = 'Channel';
const idPostfix = 'Tile';

const ProviderChannelTile = React.forwardRef<
  HTMLDivElement,
  ProviderChannelTileProps
>(
  (
    {
      channel,
      id,
      onClick,
      showMarkAsRead,
      showPinned,
    }: ProviderChannelTileProps,
    ref: React.ForwardedRef<HTMLDivElement>,
  ) => {
    const { logError } = useDatadog();
    const [isMarkAsReadLoading, setIsMarkAsReadLoading] =
      useState<boolean>(false);
    const { lastMessage, lastPinnedMessage, members, pinnedMessageIds } =
      channel ?? {};
    const pinnedMessagesCount = pinnedMessageIds?.length || 0;
    const newMessagesCount = getUnreadMessagesCount(channel);
    const isChannelPin = showPinned && pinnedMessagesCount > 0;
    const channelName = getChannelName(channel);
    const theCount = isChannelPin ? pinnedMessagesCount : newMessagesCount;
    const lastChannelMessage: UserMessage = isChannelPin
      ? (lastPinnedMessage as UserMessage)
      : (lastMessage as UserMessage);
    const isLastMessageAttachment = lastChannelMessage?.isFileMessage();
    const patientNickname = (channel.cachedMetaData as ChannelMetaData)
      ?.patientNickname;
    const getLastMessageText = useMemo(() => {
      if (lastChannelMessage?.isAdminMessage()) {
        return getAdminMessageText(
          lastChannelMessage.message,
          lastChannelMessage.sender?.nickname ?? '',
        );
      }
      return isLastMessageAttachment ? '1 file' : lastChannelMessage?.message;
    }, [lastChannelMessage]);

    return (
      <ChakraFlex
        _hover={{
          backgroundColor: 'neutral.background.secondary',
        }}
        alignItems="start"
        backgroundColor={isChannelPin && 'secondary.5'}
        borderRadius="12px"
        cursor="pointer"
        gap="12px"
        id={`${idPrefix}-container__${id}__${idPostfix}`}
        my={2}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          onClick(channel);
        }}
        padding="14px 12px"
        ref={ref}
        width="100%"
      >
        <ChakraBox
          alignItems="center"
          id={`${idPrefix}-avatar-container__${id}__${idPostfix}`}
        >
          {isExternalChannel(channel?.url) || (members ?? []).length === 0 ? (
            <Avatar
              id={`${idPrefix}-avatar__${id}__${idPostfix}`}
              name={channelName}
              userType="provider"
            />
          ) : (
            <AvatarGroup
              assignees={[
                {
                  name: members[1]?.nickname,
                  size: 'xs',
                  userType: getUserTypeFromChatUser(members[1]),
                } as AvatarProps,
                {
                  name: members[0]?.nickname,
                  size: 'xs',
                  userType: getUserTypeFromChatUser(members[0]),
                } as AvatarProps,
              ]}
              id={`${idPrefix}-avatar-group__${id}__${idPostfix}`}
            />
          )}
        </ChakraBox>
        <ChakraFlex
          alignItems="start"
          direction="column"
          id={`${idPrefix}-provider-container__${id}__${idPostfix}`}
          width="calc(100% - 52px)"
        >
          <ChakraFlex
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
            marginBottom="6px"
            width="100%"
          >
            <ChakraFlex alignItems="flex-start" gap={2}>
              {isChannelPin && (
                <Icon
                  color="accent.blue"
                  name={FAIconName.thumbTackSolid}
                  size="sm"
                />
              )}
              <Text
                color={isChannelPin ? 'accent.blue' : 'black.90'}
                id={`${idPrefix}-primary-provider-name__${id}__${idPostfix}`}
                noOfLines={1}
                variant="bodyMedium"
              >
                {channelName}
              </Text>
            </ChakraFlex>
            <ChakraFlex
              alignItems="center"
              flexDirection="row"
              gap="2px"
              justifyContent="flex-start"
            >
              <Tooltip
                id={`${id}__tag-tooltip`}
                placement="top-start"
                tooltipText={patientNickname}
                width={patientNickname ? '250px' : '120px'}
              >
                <Tag
                  color={TagColor.white}
                  id={`${id}__tag`}
                  isBorderRequired
                  key={`${id}__tag`}
                  maxWidth="102px"
                  size="sm"
                  value={patientNickname ? patientNickname : 'Patient'}
                />
              </Tooltip>
            </ChakraFlex>
          </ChakraFlex>
          <ChakraFlex
            alignItems="center"
            color="black.60"
            justifyContent="space-between"
            width="100%"
          >
            <ChakraFlex alignItems="center" gap="6px" maxWidth="240px">
              {isLastMessageAttachment && (
                <Icon
                  color={neutral.secondary}
                  name={FAIconName.fileRegular}
                  size="sm"
                />
              )}
              <Text
                flex={1}
                id={`${idPrefix}-provider-last-message__${id}__${idPostfix}`}
                overflow="hidden"
                textOverflow="ellipsis"
                variant="caption"
                whiteSpace="nowrap"
              >
                {getLastMessageText}
              </Text>
            </ChakraFlex>
            <ChakraFlex alignItems="center" gap="6px">
              {!!theCount && <Counter id={id} number={theCount} />}
              {showMarkAsRead &&
                (!isMarkAsReadLoading ? (
                  <Tooltip
                    id={`${idPrefix}-mark-as-read-tooltip__${channel.url}__${idPostfix}`}
                    placement="top-start"
                    tooltipText="Mark as read"
                    width="120px"
                  >
                    {/* TODO: revisit FireflyIconButton size + variants */}
                    <ChakraButton
                      _hover={{
                        backgroundColor: '#DCE7FF',
                        color: '#2266FF',
                      }}
                      borderRadius="5px"
                      height="20px"
                      minWidth={0}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        setIsMarkAsReadLoading(true);
                        channel
                          .markAsRead()
                          .catch((e) => {
                            console.error(
                              `could not mark as read for channel: ${channel?.url}`,
                              e,
                            );
                            logError(e, {
                              description: `could not mark as read for channel: ${channel?.url}`,
                            });
                          })
                          .finally(() => setIsMarkAsReadLoading(false));
                      }}
                      padding={0}
                    >
                      <Icon name={FAIconName.envelopeRegular} />
                    </ChakraButton>
                  </Tooltip>
                ) : (
                  <CircularProgress
                    data-testid={`${idPrefix}-circular-progress__${id}__${idPostfix}`}
                    id={`${idPrefix}-circular-progress__${id}__${idPostfix}`}
                    isIndeterminate
                    size="20px"
                  />
                ))}
              <Text
                id={`${idPrefix}-provider-last-message__${id}__${idPostfix}`}
                textAlign="right"
                variant="caption"
              >
                {lastMessage &&
                  DateTime.fromMillis(lastMessage?.createdAt).toFormat('LLL d')}
              </Text>
            </ChakraFlex>
          </ChakraFlex>
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);

ProviderChannelTile.displayName = 'ProviderChannelTile';
export default ProviderChannelTile;
